import { Rectangle, Renderer, RenderTexture, Sprite, Texture } from "pixi.js"

import { logger } from "../../../../logger"
import { getRendererApp } from "../renderer_app"

// Alpha at or below this counts as empty. Matches the threshold the death-crumble effect uses for
// the same question ("where does the art actually stop").
const CONTENT_ALPHA_THRESHOLD = 8

// Keyed by the caller's texture id. A composite tile texture is built once and shared by every
// sprite drawn from it, so its content bounds are worth computing once for the whole session --
// the readback is a GPU stall, and a null result (no renderer, e.g. in tests) is cached too so a
// failing lookup isn't retried on every sprite.
// Logical IDs can survive an art change. Texture identity prevents bounds for an older
// generation leaking into its replacement and allows retired textures to be collected.
const cache = new WeakMap<Texture, Map<string, Rectangle | null>>()
const trimmedTextures = new WeakMap<Texture, Texture>()

// Bounds measured where the pixels were made, for any caller: art rasterised on the CPU (an Ember frame is drawn in
// JS and uploaded from a buffer) never needs reading back. That readback was the castle's hitch: the occlusion
// outline met each new Ember pose that way every 300ms while walking, and the read waited 20-47ms on the GPU queue.
const known = new WeakMap<Texture, Rectangle | null>()

/** Records a texture's content bounds (in its own pixel space; null when blank), so they are never read from the GPU. */
export const seedTextureContentBounds = (texture: Texture, bounds: Rectangle | null): void => {
    known.set(texture, bounds)
}

/** Seeds a texture's bounds from the RGBA pixels (top row first) it was uploaded from. */
export const seedTextureContentBoundsFromPixels = (texture: Texture, data: ArrayLike<number>, width: number, height: number): void => {
    known.set(texture, alphaBounds(data, width, height, texture))
}

/** A tight atlas view for carried icons; leaves the shared source texture untouched. */
export const trimTextureToContent = (texture: Texture): Texture => {
    const cached = trimmedTextures.get(texture)
    if (cached) {
        return cached
    }
    // Do not cache a missing renderer: a sprite may be requested before initialization.
    try {
        if (!(getRendererApp().renderer as any)?.plugins?.extract || !texture.valid) {
            return texture
        }
    } catch {
        return texture
    }
    const content = known.has(texture) ? known.get(texture)! : measure(texture)
    if (!content) {
        trimmedTextures.set(texture, texture)
        return texture
    }
    const area = texture.trim ?? new Rectangle(0, 0, texture.orig.width, texture.orig.height)
    // Map the crop through the texture UVs, handling trimmed and rotated atlases
    // without copying pixels or uploading a second image to the GPU.
    const uv = texture._uvs
    const corners = [
        [content.x, content.y], [content.right, content.y],
        [content.x, content.bottom], [content.right, content.bottom],
    ].map(([x, y]) => {
        const u = (x - area.x) / area.width
        const v = (y - area.y) / area.height
        return {
            x: (uv.x0 + (uv.x1 - uv.x0) * u + (uv.x3 - uv.x0) * v) * texture.baseTexture.width,
            y: (uv.y0 + (uv.y1 - uv.y0) * u + (uv.y3 - uv.y0) * v) * texture.baseTexture.height,
        }
    })
    const minX = Math.min(...corners.map(p => p.x))
    const minY = Math.min(...corners.map(p => p.y))
    const width = Math.max(...corners.map(p => p.x)) - minX
    const height = Math.max(...corners.map(p => p.y)) - minY
    const trimmed = new Texture(texture.baseTexture, new Rectangle(minX, minY, width, height),
        new Rectangle(0, 0, content.width, content.height), undefined, texture.rotate)
    trimmedTextures.set(texture, trimmed)
    return trimmed
}

// The tight bounds of a texture's non-transparent pixels, in the texture's own pixel space.
//
// This exists because a COMPOSITE tile texture is the bounding box of all the tiles making up an
// object (see TileTextureExtractor), and that box is routinely much wider than the thing you can
// see: the ember campfire's box carries ~32px of empty tile to its left and right. Anything sized
// from the texture rather than its content -- a hover/hit region above all -- is then wrong by that
// much, and the entity reacts to a pointer that is nowhere near it.
export const textureContentBounds = (texture: Texture, cacheKey: string): Rectangle | null => {
    if (known.has(texture)) return known.get(texture)!
    let entries = cache.get(texture)
    if (!entries) { entries = new Map(); cache.set(texture, entries) }
    if (entries.has(cacheKey)) {
        return entries.get(cacheKey)
    }
    const bounds = measure(texture)
    entries.set(cacheKey, bounds)
    return bounds
}

// Textures waiting for an idle readback (textureContentBoundsWhenIdle), oldest first.
const idleQueue: { texture: Texture; cacheKey: string }[] = []
const queued = new WeakMap<Texture, Set<string>>()
let idleScheduled = false
// Readbacks the GPU has yet to finish (WebGL2): the art copied into a pack buffer, read once it passes `fence`.
const inFlight: { texture: Texture; cacheKey: string; buffer: WebGLBuffer; fence: WebGLSync; width: number; height: number }[] = []
/** Readbacks started per pass: each is a small draw and copy, with nothing to wait for. */
const READBACKS_PER_PASS = 8
/** How often finished readbacks are collected (ms). */
const READBACK_POLL_MS = 16
let pollScheduled = false

/**
 * The bounds if they have been read, or undefined while they wait for an idle moment. For callers
 * that ask every frame: the readback stalls the main thread on the GPU (4-15ms per texture measured,
 * a dropped frame on a modest machine), and the outline and occlusion tests met each new pose that
 * way, most often on landing behind a building.
 */
export const textureContentBoundsWhenIdle = (texture: Texture, cacheKey: string): Rectangle | null | undefined => {
    if (known.has(texture)) return known.get(texture)!
    const entries = cache.get(texture)
    if (entries?.has(cacheKey)) return entries.get(cacheKey)
    if (!texture?.valid) return null
    // Read from the image it was loaded from, on the CPU, at once: nothing to wait for and no GPU queue to drain.
    const fromImage = imageContentBounds(texture)
    if (fromImage !== undefined) {
        let entries = cache.get(texture)
        if (!entries) cache.set(texture, entries = new Map())
        entries.set(cacheKey, fromImage)
        return fromImage
    }
    // Nothing can read it (no renderer yet, or none at all in tests): there is nothing to wait for.
    if (!canReadPixels()) return null
    let keys = queued.get(texture)
    if (!keys) queued.set(texture, keys = new Set())
    if (!keys.has(cacheKey)) {
        keys.add(cacheKey)
        idleQueue.push({ texture, cacheKey })
        scheduleIdleReadback()
    }
    return undefined
}

/** Read every waiting texture now (tests, or a caller that is about to stall anyway). */
export const flushIdleContentBounds = (): void => {
    collectReadbacks(true)
    while (idleQueue.length) {
        const next = idleQueue.shift()!
        queued.get(next.texture)?.delete(next.cacheKey)
        if (next.texture.baseTexture && next.texture.valid) textureContentBounds(next.texture, next.cacheKey)
    }
}

const canReadPixels = (): boolean => {
    try {
        return !!(getRendererApp().renderer as any)?.plugins?.extract
    } catch {
        return false
    }
}

const scheduleIdleReadback = (): void => {
    if (idleScheduled) return
    idleScheduled = true
    const run = (deadline?: { timeRemaining(): number }) => {
        idleScheduled = false
        let started = 0
        do {
            const next = idleQueue.shift()
            if (!next) break
            if (!next.texture.baseTexture || !next.texture.valid) {
                queued.get(next.texture)?.delete(next.cacheKey)
                continue
            }
            if (startReadback(next.texture, next.cacheKey)) {
                started++
                continue
            }
            // No WebGL2: one blocking readback per idle period unless there is clearly room for more.
            queued.get(next.texture)?.delete(next.cacheKey)
            textureContentBounds(next.texture, next.cacheKey)
            if (!(deadline && deadline.timeRemaining() > 12)) break
        } while (idleQueue.length && started < READBACKS_PER_PASS)
        if (inFlight.length) schedulePoll()
        if (idleQueue.length) scheduleIdleReadback()
    }
    if (typeof requestIdleCallback === "function") requestIdleCallback(run, { timeout: 1000 })
    else setTimeout(run, 0)
}

const webgl2 = (): { renderer: Renderer; gl: WebGL2RenderingContext } | undefined => {
    let renderer: Renderer | undefined
    try {
        renderer = getRendererApp().renderer as Renderer
    } catch {
        return undefined
    }
    const gl = renderer?.gl as WebGL2RenderingContext | undefined
    if (!gl || renderer.context?.webGLVersion !== 2 || typeof gl.fenceSync !== "function" || gl.isContextLost()) return undefined
    return { renderer, gl }
}

/**
 * Copies the texture's pixels into a pack buffer without waiting for them: a plain readPixels
 * returns only once the GPU has worked through every command queued before it. Behind a tall
 * building that queue was most of a second deep (two seconds on a player's machine), and the frame
 * that asked for the pixels -- the one climbing a ladder -- stalled for all of it.
 */
const startReadback = (texture: Texture, cacheKey: string): boolean => {
    const context = webgl2()
    if (!context) return false
    contentBoundsReads.gpu++
    const { renderer, gl } = context
    const probe = new Sprite(texture)
    let target: RenderTexture | undefined
    try {
        target = renderer.generateTexture(probe, { resolution: 1 })
        const width = Math.floor(target.frame.width + 1e-4), height = Math.floor(target.frame.height + 1e-4)
        if (!width || !height) return false
        renderer.renderTexture.bind(target)
        const buffer = gl.createBuffer()
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer)
        gl.bufferData(gl.PIXEL_PACK_BUFFER, width * height * 4, gl.STREAM_READ)
        gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, 0)
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
        renderer.renderTexture.bind(null)
        const fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0)
        if (!buffer || !fence) {
            gl.deleteBuffer(buffer)
            return false
        }
        gl.flush()
        inFlight.push({ texture, cacheKey, buffer, fence, width, height })
        return true
    } catch (e) {
        logger.error({ tags: ["sprite", "error"] }, e)
        return false
    } finally {
        probe.destroy()
        // Queued after the copy: the GPU reads the art before it lets the target go.
        target?.destroy(true)
    }
}

/** Reads the readbacks the GPU has finished; with `all`, every one (a caller about to stall anyway). */
const collectReadbacks = (all = false): void => {
    if (!inFlight.length) return
    const context = webgl2()
    for (let i = 0; i < inFlight.length;) {
        const readback = inFlight[i]
        const { texture, cacheKey, buffer, fence, width, height } = readback
        if (!context) {
            // Lost with its context: asked for again, it is queued again.
            inFlight.splice(i, 1)
            queued.get(texture)?.delete(cacheKey)
            continue
        }
        const { gl } = context
        if (!all && gl.getSyncParameter(fence, gl.SYNC_STATUS) !== gl.SIGNALED) {
            i++
            continue
        }
        const pixels = new Uint8Array(width * height * 4)
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer)
        gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, pixels)
        gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
        gl.deleteBuffer(buffer)
        gl.deleteSync(fence)
        inFlight.splice(i, 1)
        queued.get(texture)?.delete(cacheKey)
        if (!texture.baseTexture || !texture.valid) continue
        let entries = cache.get(texture)
        if (!entries) cache.set(texture, entries = new Map())
        if (!entries.has(cacheKey)) entries.set(cacheKey, alphaBounds(pixels, width, height, texture))
    }
}

const schedulePoll = (): void => {
    if (pollScheduled) return
    pollScheduled = true
    setTimeout(() => {
        pollScheduled = false
        collectReadbacks()
        if (inFlight.length) schedulePoll()
    }, READBACK_POLL_MS)
}

/** How many bounds have been measured, and how: read by the hitch probe. */
export const contentBoundsReads = { image: 0, imageMs: 0, gpu: 0 }

// Where image-backed art is read on the CPU: `willReadFrequently` keeps the canvas in memory, off the GPU.
let scratch: CanvasRenderingContext2D | null | undefined

/**
 * The bounds read from the image a texture was loaded from (an atlas PNG), on the CPU; undefined when its pixels
 * live only on the GPU (rendered art) or cannot be read here. A GPU readback waits out every command queued before
 * it, which in a busy castle is 20-50ms; drawing a cell of an already decoded image into a canvas is a fraction of one.
 */
const imageContentBounds = (texture: Texture): Rectangle | null | undefined => {
    const source = (texture.baseTexture?.resource as any)?.source
    const isImage = (typeof HTMLImageElement !== "undefined" && source instanceof HTMLImageElement) ||
        (typeof ImageBitmap !== "undefined" && source instanceof ImageBitmap)
    if (!isImage || texture.rotate) return undefined
    const resolution = texture.baseTexture.resolution || 1
    const { x, y, width, height } = texture.frame
    const w = Math.round(width * resolution), h = Math.round(height * resolution)
    if (!w || !h) return undefined
    try {
        scratch ||= document.createElement("canvas").getContext("2d", { willReadFrequently: true } as any) as CanvasRenderingContext2D | null
        if (!scratch) return undefined
        const canvas = scratch.canvas
        if (canvas.width < w) canvas.width = w
        if (canvas.height < h) canvas.height = h
        const t0 = performance.now()
        scratch.clearRect(0, 0, w, h)
        scratch.drawImage(source, x * resolution, y * resolution, w, h, 0, 0, w, h)
        const bounds = alphaBounds(scratch.getImageData(0, 0, w, h).data, w, h, texture)
        contentBoundsReads.image++
        contentBoundsReads.imageMs += performance.now() - t0
        return bounds
    } catch {
        return undefined
    }
}

/** A texture's bounds, from its image when it has one, else read back from the GPU. */
const measure = (texture: Texture): Rectangle | null => {
    const fromImage = texture?.valid ? imageContentBounds(texture) : undefined
    return fromImage !== undefined ? fromImage : computeContentBounds(texture)
}

const computeContentBounds = (texture: Texture): Rectangle | null => {
    let extract: any
    try {
        extract = (getRendererApp().renderer as any)?.plugins?.extract
    } catch {
        return null
    }
    if (!extract || !texture?.valid) {
        return null
    }
    contentBoundsReads.gpu++
    const probe = new Sprite(texture)
    try {
        const canvas: HTMLCanvasElement = extract.canvas(probe)
        const ctx = canvas.getContext("2d")
        const canvasWidth = canvas.width
        const canvasHeight = canvas.height
        if (!ctx || canvasWidth === 0 || canvasHeight === 0) {
            return null
        }
        return alphaBounds(ctx.getImageData(0, 0, canvasWidth, canvasHeight).data, canvasWidth, canvasHeight, texture)
    } catch (e) {
        logger.error({ tags: ["sprite", "error"] }, e)
        return null
    } finally {
        probe.destroy()
    }
}

/** The bounds of the pixels above the alpha threshold in RGBA rows (top first) read from `texture`. */
const alphaBounds = (data: ArrayLike<number>, width: number, height: number, texture: Texture): Rectangle | null => {
    let minX = width
    let minY = height
    let maxX = -1
    let maxY = -1
    for (let y = 0; y < height; y++) {
        const rowOffset = y * width * 4
        for (let x = 0; x < width; x++) {
            if (data[rowOffset + x * 4 + 3] > CONTENT_ALPHA_THRESHOLD) {
                if (x < minX) minX = x
                if (x > maxX) maxX = x
                if (y < minY) minY = y
                if (y > maxY) maxY = y
            }
        }
    }
    if (maxX < 0) {
        // Entirely transparent (a blank stand-in marker). No content to point at.
        return null
    }

    // The pixels can be at a different resolution than the texture, so map back through
    // their own dimensions rather than assuming 1:1.
    const area = texture.trim ?? new Rectangle(0, 0, texture.orig.width, texture.orig.height)
    const scaleX = area.width / width
    const scaleY = area.height / height
    return new Rectangle(
        area.x + minX * scaleX,
        area.y + minY * scaleY,
        (maxX - minX + 1) * scaleX,
        (maxY - minY + 1) * scaleY,
    )
}

/**
 * Tight visible-art bounds in screen space; also handles anchored, mirrored and rotated sprites.
 * Undefined until the texture has been read, which happens when the browser is idle: callers ask
 * every frame. Null when it has no visible pixels (or nothing can read them).
 */
export const spriteContentWorldBounds = (sprite: Sprite, out: Rectangle): Rectangle | null | undefined => {
    const content = textureContentBoundsWhenIdle(sprite.texture, "occlusion")
    if (!content) return content
    // The caller's broad bounds query has already updated the world transform.
    const matrix = sprite.worldTransform
    const x = content.x - sprite.anchor.x * sprite.texture.orig.width
    const y = content.y - sprite.anchor.y * sprite.texture.orig.height
    const originX = matrix.a * x + matrix.c * y + matrix.tx
    const originY = matrix.b * x + matrix.d * y + matrix.ty
    const widthX = matrix.a * content.width, widthY = matrix.b * content.width
    const heightX = matrix.c * content.height, heightY = matrix.d * content.height
    out.x = originX + Math.min(0, widthX) + Math.min(0, heightX)
    out.y = originY + Math.min(0, widthY) + Math.min(0, heightY)
    out.width = Math.abs(widthX) + Math.abs(heightX)
    out.height = Math.abs(widthY) + Math.abs(heightY)
    return out
}
